import React, { useState } from 'react';
import { Button, Grid, Icon, Message, Popup, Table } from 'semantic-ui-react';
import { FieldArray, Formik } from 'formik';

import CommonTagsInputWrapper from './CommonTagsInputWrapper';
import { useTranslation } from 'react-i18next';
import ButtonWrapper from './ButtonWrapper';
import { personsActions } from '../../behavior/persons/index';
import { getObjPropRecursively } from '../../utils';
import { Mode } from '../../constants';
import { AdvancedFieldError } from 'react-lib/dist/components-es';
import { connect } from 'react-redux';
import { ReactNode } from 'react';
import { CompetencyInfoMap } from '../../behavior/persons/competencyApi';

type Props = {
  showEmailHandler?: boolean;
  showNoEmailAvailable?: boolean;
  showDeleteHandler?: boolean;
  fieldPropName?: string;
  showAddNew?: boolean;
  showRole?: boolean;
  matchPropName?: string;
  sort?: boolean;
  isNotMissingCompetencies?: boolean;
  isExposedPesons?: boolean;
  // Legacy props from when PersonListContainer rendered an "override" toggle.
  // No longer used (the toggle moved to Batch Update's MUI segmented control)
  // but kept here so existing call sites that still thread them through compile.
  overridePersonState?: boolean;
  setOverridePersonState?: (arg: boolean) => void;
  includeNotAtCernInSearch?: boolean;
  errors: any;
  validateOnChange: any;
  arrayPropName: any;
  mode: any;
  values: any;
  disabled?: boolean;
  showPersonalData?: boolean;
  newPersonEntity: any;
  showNotPresentAtCernWarning?: boolean;
  showExpiredCompetencyWarning?: boolean;
  competencyInfoMap?: CompetencyInfoMap;
  renderSuggestionAddition?: (obj: any) => ReactNode;
  blockExpiredSelection?: boolean;
};

const PersonListContainer: React.FC<Props> = ({
  showEmailHandler = false,
  showNoEmailAvailable = false,
  showDeleteHandler = false,
  fieldPropName = '',
  showAddNew = false,
  showRole = false,
  matchPropName = 'name',
  sort = false,
  isNotMissingCompetencies = false,
  isExposedPesons = false,
  showNotPresentAtCernWarning = false,
  showExpiredCompetencyWarning = false,
  competencyInfoMap = {},
  renderSuggestionAddition,
  blockExpiredSelection = false,
  includeNotAtCernInSearch = false,
  errors,
  validateOnChange,
  arrayPropName,
  mode,
  values,
  disabled,
  showPersonalData,
  newPersonEntity,
}) => {
  const [newPerson, setNewPerson] = useState(new newPersonEntity());
  const [clearInputCounter, setClearInputCounter] = useState<number>(0);

  const { t } = useTranslation();

  if (sort) {
    sortPersonList(getObjPropRecursively(values, arrayPropName), fieldPropName, matchPropName);
  }

  const getWarning = () => {
    return (
      mode != Mode.VIEW &&
      isNotMissingCompetencies && (
        <Grid.Column width={16}>
          <div className="containerReducedWhiteSpace">
            <Message color="yellow">
              {t('person.list.container.warning.is.not.missing.competency.msg')}
            </Message>
          </div>
        </Grid.Column>
      )
    );
  };

  const getTableHeader = () => {
    return (
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>{t('person.list.container.name.column.header')}</Table.HeaderCell>
          <Table.HeaderCell />
          <Table.HeaderCell />
          <Table.HeaderCell />
          <Table.HeaderCell />
        </Table.Row>
      </Table.Header>
    );
  };

  const clearNewContact = () => {
    setClearInputCounter((prev: number) => prev + 1);
  };

  const removeContact = (index: number, remove: any) => {
    remove(index);
  };

  const emailHandler = (index: number, person: any) => {
    window.location.href = 'mailto:' + person.email;
  };

  const getRole = (p: any) => {
    return showRole && p.role ? '(' + p.role + ') ' : '';
  };

  const getTableRow = (index: number, person: any, remove: any) => {
    if (isExposedPesons && new Date(person.endDate) <= new Date()) {
      return null;
    }

    let p = getObjPropRecursively(person, fieldPropName);
    p = p ? p : person;

    let showDeleteHandlerRow = showDeleteHandler && !p.cannotBeDeleted && mode !== Mode.VIEW;

    const getNotPresentAtCernWarning = () => {
      return (
        <Popup
          trigger={<Icon name="warning sign" className="warningIconNotAtCern" />}
          on={['hover']}
          size="mini"
          content={t('message.person.not.present.at.cern')}
        />
      );
    };

    const hasExpiredCompetency = showExpiredCompetencyWarning && p.id && competencyInfoMap[p.id]?.isMissingCompetency;

    const getExpiredCompetencyWarning = () => {
      return (
        <Popup
          trigger={<Icon name="warning sign" className="warningIconExpiredCompetency" />}
          on={['hover']}
          size="mini"
          content={t('message.person.missing.competencies')}
        />
      );
    };

    const rowClassName = (() => {
      if (showNotPresentAtCernWarning && p.atCern === 'N') return 'personListContainerNotAtCernRow';
      if (hasExpiredCompetency) return 'personListContainerExpiredCompetencyRow';
      return '';
    })();

    return (
      <Table.Row
        key={index}
        className={rowClassName}
      >
        <Table.Cell>
          {showNotPresentAtCernWarning && p.atCern === 'N' && getNotPresentAtCernWarning()}
          {hasExpiredCompetency && getExpiredCompetencyWarning()}
          {getRole(p) + (showPersonalData ? p.name : p.name.replace(/[a-z]/g, 'x'))}
        </Table.Cell>
        <Table.Cell>
          {p.telephone1 ? <Icon name="phone" /> : null}
          {showPersonalData === true ? p.telephone1 : 'XXXXX'}
        </Table.Cell>
        <Table.Cell>
          {p.portablePhone ? <Icon name="mobile alternate" /> : null}
          {showPersonalData === true ? p.portablePhone : p.portablePhone && 'XXXXX'}
        </Table.Cell>
        {(showEmailHandler || showDeleteHandlerRow || showNoEmailAvailable) && (
          <Table.Cell>
            <Button.Group className="mini">
              {showEmailHandler && p.email && (
                <ButtonWrapper
                  icon="mail"
                  color="blue"
                  onClick={() => emailHandler(index, p)}
                  disabled={false}
                />
              )}
              {showNoEmailAvailable && !p.email && (
                <Popup
                  key="no.email.available"
                  trigger={<Button size="mini" icon="dont" color="red" disabled={false} />}
                  on={['hover']}
                  size="mini"
                  content={t('person.list.container.no.email.available')}
                  position="bottom left"
                />
              )}
              {showDeleteHandlerRow && (
                <ButtonWrapper
                  icon="remove"
                  color="red"
                  onClick={() => removeContact(index, remove)}
                  disabled={disabled}
                />
              )}
            </Button.Group>
          </Table.Cell>
        )}
      </Table.Row>
    );
  };

  const getTableBody = (remove: any) => {
    return (
      <Table.Body>
        {getObjPropRecursively(values, arrayPropName).map((person: any, index: number) =>
          getTableRow(index, person, remove)
        )}
      </Table.Body>
    );
  };

  const isNewPersonValid = (newPerson: number) => {
    const newPersonFieldProp = getObjPropRecursively(newPerson, fieldPropName);
    return (
      newPerson &&
      newPersonFieldProp &&
      newPersonFieldProp.id &&
      !getObjPropRecursively(values, arrayPropName)
        .map((p: any) => {
          p = getObjPropRecursively(p, fieldPropName);
          return p.id;
        })
        .includes(newPersonFieldProp.id)
    );
  };

  const getAddNewPerson = (push: any) => {
    return (
      <Formik
        initialValues={newPerson}
        enableReinitialize
        onSubmit={(values, actions) => {
          const personField = getObjPropRecursively(values.newPerson, fieldPropName);
          if (blockExpiredSelection && personField?.isMissingCompetencies) {
            clearNewContact();
            actions.setSubmitting(false);
            return;
          }
          if (isNewPersonValid(values.newPerson)) {
            push(values.newPerson);
          }
          clearNewContact();
          actions.setSubmitting(false);
        }}
        render={({ setFieldValue, handleSubmit }) => (
          <>
            <Grid.Column width={10}>
              <CommonTagsInputWrapper
                name={'newPerson' + (fieldPropName != '' ? '.' + fieldPropName : '')}
                setFieldValue={setFieldValue}
                dynamicSuggestionListAction={(v: any) =>
                  personsActions.getPersonAutocompleteListAction(
                    v,
                    isNotMissingCompetencies,
                    showPersonalData,
                    includeNotAtCernInSearch
                  )
                }
                matchProperty={matchPropName}
                displayProps={matchPropName}
                maxTags={1}
                onlyUnique={true}
                clearInputCounter={clearInputCounter}
                disabled={disabled}
                placeholder="tags.input.default.placeholder"
                className="inputWithInputDefaultButton"
                onSelect={() => {
                  setTimeout(() => {
                    handleSubmit();
                  }, 0);
                }}
                renderSuggestionAddition={renderSuggestionAddition}
              />
            </Grid.Column>
          </>
        )}
      />
    );
  };

  return (
    <FieldArray
      name={arrayPropName}
      validateOnChange={validateOnChange}
      render={({ push, remove }) => (
        <Grid stackable columns={3}>
          <Grid.Row>
            {getWarning()}
            <Grid.Column width={16}>
              <Table basic unstackable className="reducedWhiteSpaceTable">
                {getTableHeader()}
                {getTableBody(remove)}
              </Table>
              <AdvancedFieldError messages={errors[arrayPropName]} />
            </Grid.Column>
          </Grid.Row>
          {showAddNew && mode !== Mode.VIEW && <Grid.Row>{getAddNewPerson(push)}</Grid.Row>}
        </Grid>
      )}
    />
  );
};

const mapStateToProps = (state: any) => ({
  showPersonalData: state.dataPrivacy.showPersonalData,
});

const sortPersonList = (list: any, fieldPropName: string, matchPropName: string) => {
  let propName = (fieldPropName != '' ? fieldPropName + '.' : '') + matchPropName;

  list.sort((a: any, b: any) =>
    getObjPropRecursively(a, propName) > getObjPropRecursively(b, propName) ? 1 : -1
  );
};

export default connect(mapStateToProps, null)(PersonListContainer);
